import { ChainId, PUBLIC_RPCS } from '@shared/utils/config/networks';
import { ethers } from 'ethers';
import { executeDirectTransactionsEVM } from '@shared/utils/evm/executeDirectTransactionsEVM';
import { VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { TransactionBlock } from '@mysten/sui.js/transactions';
import { BlockchainType } from '@shared/types/services/enums';
import { getVMTypeFromOrderType } from '@shared/utils/validation/network-validation';
import { getSuiClient } from '@shared/utils/network/getRpcClient';
import { toast } from 'sonner';
import executeViaClient from './executeViaClient';
import executeViaExecutor from './executeViaExecutor';
import { delay } from '@/hooks/LitSessionContext/useLitAuth';
export async function executeOrderWrapper({
  params,
  baseUrl = '/api/wrapper?target=',
  environment,
  sessionSigs,
  pkp,
  cloudWallet,
  orderType,
  dbOrderParams,
  transferParams,
  signer,
  keypair,
  suiWallet
}) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
  if (!params) {
    return {
      error: 'No params provided'
    };
  }
  if (!pkp) {
    return {
      error: 'No pkp provided'
    };
  }
  if (!cloudWallet) {
    return {
      error: 'No cloudWallet provided'
    };
  }
  console.log('executing order orderwrapper params', params);
  // if !isSponsored, execute via client
  // if isSponsored & EVM, sponsor is done via executor, allowance and swap is done via client
  // if isSponsored & Solana, transactions are presigned on client, and then everything is sent via executor via Jito
  const transactions = params === null || params === void 0 ? void 0 : params.transactions;
  const isWithdrawal = (transactions === null || transactions === void 0 ? void 0 : transactions.transfer) ? true : false;
  const isSponsored = BigInt(params.fees.sponsorNative) > BigInt(0);
  const txType = getVMTypeFromOrderType(orderType);
  console.log(`TX_TYPE`, txType, `IS_SPONSORED`, isSponsored, `IS_WITHDRAWAL`, isWithdrawal, transactions.swap);
  if (txType == BlockchainType.UNKNOWN) {
    return {
      error: 'Unknown transaction type'
    };
  }
  // const isFlashBots =
  //   transactions?.swap?.chainId == 1 || transactions?.transfer?.chainId == 1;
  const isFlashBots = false;
  console.log('Executing order orderwrapper isSponsored', isSponsored, 'txType', txType, 'isWithdrawal', isWithdrawal);
  const txnKey = isWithdrawal ? 'transfer' : 'swap';
  if (isSponsored && txType == BlockchainType.SVM) {
    params.transactions[txnKey] = (_b = (_a = params === null || params === void 0 ? void 0 : params.transactions) === null || _a === void 0 ? void 0 : _a[txnKey]) === null || _b === void 0 ? void 0 : _b.map((txn, idx) => {
      const vTxn = VersionedTransaction.deserialize(bs58.decode(txn));
      try {
        vTxn.sign([keypair]);
        return bs58.encode(vTxn.serialize());
      } catch (e) {
        console.log('Executing order signed solana txn on client e', idx, e);
        // Will fail for txns that only require sponsor signature so just return txn
        return txn;
      }
    });
  }
  // Function to get orchestrator's gas objects
  async function getOrchestratorGasObjects(orchestratorAddress, client) {
    // Get all coins owned by orchestrator
    const coins = await client.getCoins({
      owner: orchestratorAddress,
      coinType: '0x2::sui::SUI' // SUI coin type
    });
    // Find suitable coins for gas payment
    return coins.data.map(coin => ({
      objectId: coin.coinObjectId,
      version: coin.version,
      digest: coin.digest
    }));
  }
  // Modified sponsored transaction code
  if (isSponsored && txType == BlockchainType.MOVE) {
    if (isWithdrawal) {
      console.log('Starting withdrawal process');
      try {
        // We dont need to modify the transactions for withdrawal at all
        // We just need to sign the transactions and return them
        console.log('Transactions to process:', (_d = (_c = params === null || params === void 0 ? void 0 : params.transactions) === null || _c === void 0 ? void 0 : _c[txnKey]) === null || _d === void 0 ? void 0 : _d.length);
        const signaturePromises = (_f = (_e = params === null || params === void 0 ? void 0 : params.transactions) === null || _e === void 0 ? void 0 : _e[txnKey]) === null || _f === void 0 ? void 0 : _f.map(async (txn, index) => {
          console.log(`Processing transaction ${index}`);
          try {
            console.log(`Creating TransactionBlock for index ${index}`);
            const txBlock = TransactionBlock.from(txn);
            console.log(`Building transaction ${index}`);
            const client = getSuiClient();
            await txBlock.build({
              client
            });
            console.log(`Attempting to sign transaction ${index}`);
            const signatureResponse = await txBlock.sign({
              signer: suiWallet
            });
            console.log(`Successfully signed transaction ${index}`);
            console.log(`Serializing transaction ${index}`);
            const serialized = txBlock.serialize();
            console.log(`Successfully serialized transaction ${index}`);
            return {
              index,
              result: {
                transactionBlock: serialized,
                signature: signatureResponse.signature,
                userAddress: suiWallet.toSuiAddress()
              }
            };
          } catch (err) {
            console.error(`Error processing transaction ${index}:`, err);
            throw err;
          }
        });
        console.log('Created all signature promises');
        try {
          console.log('Awaiting all signatures');
          const signatures = await Promise.all(signaturePromises);
          console.log('Successfully got all signatures');
          console.log('Sorting and mapping results');
          params.transactions[txnKey] = signatures.sort((a, b) => a.index - b.index).map(item => item.result);
          console.log('Successfully processed all transactions');
        } catch (err) {
          console.error('Error in Promise.all:', err);
          throw err;
        }
      } catch (err) {
        console.error('Top level error in withdrawal process:', err);
        throw err;
      }
    } else {
      const signaturePromises = (_h = (_g = params === null || params === void 0 ? void 0 : params.transactions) === null || _g === void 0 ? void 0 : _g[txnKey]) === null || _h === void 0 ? void 0 : _h.map(async (txn, index) => {
        const client = getSuiClient();
        // Get orchestrator's gas objects
        const gasObjects = await getOrchestratorGasObjects(params.orchestrator.suiAddress, client);
        const txBlock = TransactionBlock.from(txn);
        const kindBytes = await txBlock.build({
          onlyTransactionKind: true
        });
        const sponsoredTx = TransactionBlock.fromKind(kindBytes);
        sponsoredTx.setSender(suiWallet.toSuiAddress());
        sponsoredTx.setGasOwner(params.orchestrator.suiAddress);
        // Set gas payment with orchestrator's coins
        sponsoredTx.setGasPayment(gasObjects);
        // Keep original gas config values
        const originalGasConfig = txBlock.blockData.gasConfig;
        sponsoredTx.setGasPrice(Number(originalGasConfig.price));
        sponsoredTx.setGasBudget(Number(originalGasConfig.budget));
        return sponsoredTx.sign({
          signer: suiWallet
        }).then(signatureResponse => ({
          index,
          result: {
            transactionBlock: sponsoredTx.serialize(),
            signature: signatureResponse.signature,
            userAddress: suiWallet.toSuiAddress()
          }
        }));
      });
      const signatures = await Promise.all(signaturePromises);
      params.transactions[txnKey] = signatures.sort((a, b) => a.index - b.index).map(item => item.result);
    }
  }
  // if (isFlashBots) {
  //   const sponsorTransactions = [params.transactions.sponsor]?.filter(x => x)
  //   const allowanceTransactions = [params.transactions.allowance]?.filter(x => x)
  //   const swapTransactions = [transactions?.swap || transactions?.transfer]?.filter(x => x)
  //   if (params?.transactions?.allowance?.length) {
  //     params.transactions.allowance = await Promise.all(
  //       params.transactions.allowance.map(async (x) => {
  //         return await signer.signTransaction(x);
  //       })
  //     );
  //   }
  //   if (params?.transactions?.swap?.[0]) {
  //     params.transactions.swap[0] = await signer.signTransaction(transactions?.swap?.[0]);
  //   }
  //   if (params?.transactions?.transfer?.[0]) {
  //     params.transactions.transfer[0] = await signer.signTransaction(transactions?.transfer?.[0]);
  //   }
  //   orderType = 'SWAP_FLASHBOTS'
  // }
  console.log('Executing order start', orderType, params, cloudWallet);
  try {
    if (!isSponsored && !isFlashBots) {
      console.log('Executing order executing via client', orderType);
      return await executeViaClient({
        params,
        pkp,
        orderType,
        dbOrderParams,
        signer,
        keypair,
        suiKeyPair: suiWallet,
        blockchainType: txType,
        transferParams
      });
      //execute locally (CLIENT)
    } else if (isSponsored && txType == BlockchainType.EVM && !isFlashBots) {
      console.log('Executing order executing executor + client' + orderType);
      const evmTransactions = transactions;
      const sponsorTx = evmTransactions.sponsor;
      console.log('Executing order hasSponsor', sponsorTx);
      let decodedResponse = null;
      let additionalExecutionTime = 0;
      if (isSponsored) {
        console.log('Executing order sponsor via lit');
        const startTime = Date.now();
        const data = await executeViaExecutor({
          params,
          pkp,
          orderType: orderType,
          cloudWallet,
          dbOrderParams,
          transferParams
        });
        toast.info('Hang tight, this order is taking a little longer than usual');
        decodedResponse = (_j = data === null || data === void 0 ? void 0 : data.result) === null || _j === void 0 ? void 0 : _j.decodedResponse;
        console.log('Executing order data', data);
        console.log('Executing order decodedResponse', decodedResponse);
        if (decodedResponse.error || !decodedResponse || (decodedResponse === null || decodedResponse === void 0 ? void 0 : decodedResponse.status) == 'error') {
          return {
            error: 'Sponsor Transaction Failed'
          };
        }
        const sponsorTxnHash = ((_k = decodedResponse === null || decodedResponse === void 0 ? void 0 : decodedResponse.sponsorTransaction) === null || _k === void 0 ? void 0 : _k.hash) || ((_m = (_l = decodedResponse === null || decodedResponse === void 0 ? void 0 : decodedResponse.sponsorTransactions) === null || _l === void 0 ? void 0 : _l[0]) === null || _m === void 0 ? void 0 : _m.hash);
        const network = (sponsorTx === null || sponsorTx === void 0 ? void 0 : sponsorTx.chainId) ? sponsorTx.chainId : ChainId.SUI;
        console.log('Executing order awaiting sponsor to be mined', network, PUBLIC_RPCS[network], sponsorTxnHash);
        const mineStartTime = Date.now();
        const provider = new ethers.providers.JsonRpcProvider(PUBLIC_RPCS[network]);
        // Wait for one block confirmation
        const receipt = await provider.waitForTransaction(sponsorTxnHash, 1);
        if (!receipt || receipt.status == 0) {
          console.log('Executing Order: Sponsor Txn Failed', receipt);
          return {
            error: 'Sponsor Txn Failed'
          };
        }
        console.log('Executing Order: Sponsor Txn Mined', receipt, 'time', Date.now() - mineStartTime);
        await delay(200);
        //Aaiting for sponsor to be mined
        additionalExecutionTime = Date.now() - startTime;
      }
      additionalExecutionTime = isSponsored ? additionalExecutionTime : 0;
      try {
        const clientExecutionResponse = await executeViaClient({
          params,
          pkp,
          orderType,
          dbOrderParams,
          signer,
          additionalDecodedResponse: decodedResponse,
          additionalExecutionTime,
          keypair,
          suiKeyPair: suiWallet,
          blockchainType: txType,
          transferParams
        });
        decodedResponse = (_p = (_o = clientExecutionResponse === null || clientExecutionResponse === void 0 ? void 0 : clientExecutionResponse.dbOrderHistory) === null || _o === void 0 ? void 0 : _o.result) === null || _p === void 0 ? void 0 : _p.decodedResponse;
        console.log('Executing order evm + sponsor pipeline clientExecutionResponse', clientExecutionResponse);
        if ((decodedResponse === null || decodedResponse === void 0 ? void 0 : decodedResponse.error) || (clientExecutionResponse === null || clientExecutionResponse === void 0 ? void 0 : clientExecutionResponse.error) || ((_q = clientExecutionResponse === null || clientExecutionResponse === void 0 ? void 0 : clientExecutionResponse.result) === null || _q === void 0 ? void 0 : _q.error) || !(clientExecutionResponse === null || clientExecutionResponse === void 0 ? void 0 : clientExecutionResponse.result)) {
          throw new Error(clientExecutionResponse.error || (decodedResponse === null || decodedResponse === void 0 ? void 0 : decodedResponse.error));
        }
        return clientExecutionResponse;
      } catch (e) {
        console.log('Executing Order error executing via client', e);
        if (sponsorTx) {
          const txs = transactions;
          const sponsorTx = txs.sponsor;
          try {
            const provider = new ethers.providers.JsonRpcProvider(PUBLIC_RPCS[sponsorTx.chainId]);
            const unsignedTxnSendFundsToBackOrchestrator = {
              from: sponsorTx.to,
              to: sponsorTx.from,
              value: BigInt(sponsorTx.value),
              gasLimit: BigInt(sponsorTx.gasLimit),
              data: sponsorTx.data,
              gasPrice: BigInt(sponsorTx.gasPrice),
              chainId: sponsorTx.chainId
            };
            let gasPrice = BigInt(sponsorTx.gasPrice);
            let gasLimit = BigInt(sponsorTx.gasLimit);
            // Apply Arbitrum-specific logic
            if (sponsorTx.chainId === ChainId.ARBITRUM) {
              const blockBaseFee = await provider.getGasPrice();
              if (gasPrice < BigInt(blockBaseFee === null || blockBaseFee === void 0 ? void 0 : blockBaseFee.toString())) {
                // Increase gasPrice to be slightly above block base fee
                gasPrice = BigInt(blockBaseFee === null || blockBaseFee === void 0 ? void 0 : blockBaseFee.toString()) * BigInt(150) / BigInt(100); // 10% higher than base fee
                // Recalculate gasLimit to maintain the same total gas cost
                const originalGasCost = BigInt(sponsorTx.gasPrice) * gasLimit;
                gasLimit = originalGasCost / gasPrice;
                console.log('Adjusted gas settings for Arbitrum:', {
                  gasPrice: gasPrice === null || gasPrice === void 0 ? void 0 : gasPrice.toString(),
                  gasLimit: gasLimit === null || gasLimit === void 0 ? void 0 : gasLimit.toString()
                });
              }
            }
            const userBalance = await provider.getBalance(sponsorTx.to);
            const totalGasCost = gasPrice * gasLimit;
            const totalCost = BigInt(sponsorTx.value) + totalGasCost;
            let updatedValue;
            console.log('Executing Order userBalance revert', userBalance === null || userBalance === void 0 ? void 0 : userBalance.toString(), 'Executing Order totalCost revert', totalCost === null || totalCost === void 0 ? void 0 : totalCost.toString());
            if (ethers.BigNumber.from(totalCost) > userBalance) {
              // If user balance is not enough, adjust the value to leave user with 0 balance
              if (userBalance > ethers.BigNumber.from(totalGasCost)) {
                updatedValue = BigInt(userBalance === null || userBalance === void 0 ? void 0 : userBalance.toString()) - totalGasCost;
              } else {
                // If user doesn't even have enough for gas, set value to 0
                updatedValue = BigInt(0);
                // You might want to handle this case differently, as the transaction will likely fail
                console.log('Executing Order: User does not have enough balance to cover gas costs for refund transaction');
                throw new Error('User does not have enough balance to cover gas costs for refund transaction');
              }
            } else {
              updatedValue = BigInt(sponsorTx.value);
            }
            unsignedTxnSendFundsToBackOrchestrator.value = updatedValue === null || updatedValue === void 0 ? void 0 : updatedValue.toString();
            unsignedTxnSendFundsToBackOrchestrator.gasLimit = gasLimit === null || gasLimit === void 0 ? void 0 : gasLimit.toString();
            unsignedTxnSendFundsToBackOrchestrator.gasPrice = gasPrice === null || gasPrice === void 0 ? void 0 : gasPrice.toString();
            const formattedUserBalance = ethers.utils.formatEther(userBalance);
            console.log('Executing Order Send fund back to orchestrator', unsignedTxnSendFundsToBackOrchestrator, 'formattedUserBalance', formattedUserBalance);
            const executionResult = await executeDirectTransactionsEVM({
              transactions: [unsignedTxnSendFundsToBackOrchestrator],
              wallet: signer,
              network: sponsorTx.chainId,
              blockConfirmations: 0,
              legacyPayloads: [{
                gasPrice: gasPrice === null || gasPrice === void 0 ? void 0 : gasPrice.toString(),
                gasLimit: gasLimit === null || gasLimit === void 0 ? void 0 : gasLimit.toString()
              }]
            });
            console.log('Executing Order Send fund back to orchestrator result', executionResult);
          } catch (e) {
            console.error('Error sending funds back to orchestrator', e);
            return {
              error: 'Error sending funds back to orchestrator'
            };
          }
          return {
            error: 'Error executing order'
          };
        }
      }
    } else {
      console.log('Executing order executing via executor', orderType);
      return executeViaExecutor({
        baseUrl,
        environment,
        params,
        sessionSigs,
        pkp,
        orderType,
        cloudWallet,
        dbOrderParams,
        transferParams
      });
    }
  } catch (error) {
    console.log(`Executing order last wrapper Error executing ${orderType}`, error);
    return {
      error: error.message
    };
  }
}